import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import useUrlTableState from '../../../hooks/useUrlTableState';
import { exportToCSV, downloadFromBackend, generateExportFilename } from '../../../utils/exportUtils';
import { useAuth } from '../../../context/AuthContext';
import orientationService from '../../../api/services/orientationService';
import locationService from '../../../api/services/locationService';
import { useCustomAlert } from '../../../hooks/useCustomAlert';
import CustomAlert from '../../../components/common/CustomAlert';
import ExportModal from "../../../components/common/ExportModal";
import SelectedExportFormatBar from '../../../components/common/SelectedExportFormatBar';
import WarningPopup from "../../../components/WarningPopup";
import {
  Calendar,
  MapPin,
  Clock,
  CheckCircle,
  AlertCircle,
  Video,
  Search,
  Download,
  Eye,
  Edit,
  Plus,
  Upload,
  Send,
  Trash2,
  Play,
  X,
  FileText,
  Square,
  CheckSquare,
  ArrowUp,
  ArrowDown,
  ArrowUpDown
} from "lucide-react";
import Button from "../../../components/common/Button/Button";
import PermissionGate from "../../../components/common/PermissionGate/PermissionGate";
import { usePermissions } from '../../../hooks/usePermissions';
import { formatDate } from '../../../utils/stringUtils';
import { formatIndianNumber } from '../../../utils/amountFormatter';
import DateRangeFilter from '../../../components/common/DateRangeFilter';
import BulkUploadModal from '../../../components/common/BulkUploadModal';
import PaginationControls from '../../../components/common/Pagination/PaginationControls';

const ORIENTATION_TABS = ['upcoming', 'ongoing', 'completed'];

const Orientation = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasPermission, canCreate } = usePermissions();
  const { alertState, showSuccess, showError, hideAlert } = useCustomAlert();

  const defaultFilters = useMemo(() => ({
    page: 1,
    limit: 20,
    search: '',
    type: 'all',
    location: 'all',
    startDate: '',
    endDate: '',
    orientationTab: 'upcoming',
  }), []);

  const {
    filters: orientationFilters,
    searchTerm: searchQuery,
    setSearchTerm: setSearchQuery,
    updateFilter: updateOrientationFilter,
    updatePage: updateOrientationPage,
    updateLimit: updateOrientationLimit,
    resetFilters: resetOrientationFilters,
  } = useUrlTableState(defaultFilters);

  const searchTerm = searchQuery;
  const setSearchTerm = setSearchQuery;

  const filterType = orientationFilters.type || 'all';
  const setFilterType = useCallback((type) => {
    updateOrientationFilter({ type });
  }, [updateOrientationFilter]);

  const filterLocation = orientationFilters.location || 'all';
  const setFilterLocation = useCallback((location) => {
    updateOrientationFilter({ location });
  }, [updateOrientationFilter]);

  const dateRange = useMemo(() => ({
    startDate: orientationFilters.startDate || '',
    endDate: orientationFilters.endDate || ''
  }), [orientationFilters.startDate, orientationFilters.endDate]);

  const setDateRange = useCallback((range) => {
    updateOrientationFilter({ startDate: range.startDate, endDate: range.endDate });
  }, [updateOrientationFilter]);

  const currentPage = orientationFilters.page;
  const setCurrentPage = useCallback((page) => {
    updateOrientationPage(page);
  }, [updateOrientationPage]);

  const itemsPerPage = orientationFilters.limit;
  const setItemsPerPage = useCallback((limit) => {
    updateOrientationLimit(limit);
  }, [updateOrientationLimit]);

  const activeTab = ORIENTATION_TABS.includes(orientationFilters.orientationTab)
    ? orientationFilters.orientationTab
    : 'upcoming';

  const setActiveTab = useCallback((tab) => {
    if (ORIENTATION_TABS.includes(tab)) {
      updateOrientationFilter({ orientationTab: tab });
    }
  }, [updateOrientationFilter]);

  const [isExporting, setIsExporting] = useState(false);
  const [orientations, setOrientations] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // const [statusUpdateInProgress, setStatusUpdateInProgress] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteOrientationId, setDeleteOrientationId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [statistics, setStatistics] = useState({
    total: 0,
    scheduled: 0,
    ongoing: 0,
    completed: 0,
    cancelled: 0
  });

  const KpiCard = ({ icon: Icon, title, value, color }) => {
    let bgColor = "bg-[#0F2942]";
    if (color?.includes("brand") || color?.includes("blue")) bgColor = "bg-[#0F2942]";
    else if (color?.includes("red")) bgColor = "bg-[#DC2626]";
    else if (color?.includes("green") || color?.includes("emerald")) bgColor = "bg-[#16A34A]";
    else if (color?.includes("purple") || color?.includes("indigo")) bgColor = "bg-[#7C3AED]";
    else if (color?.includes("yellow") || color?.includes("orange") || color?.includes("amber")) bgColor = "bg-[#D97706]";

    return (
      <div className="bg-[#F8FAFC] rounded-2xl p-6 border border-gray-100 flex items-center justify-between shadow-sm transition-all duration-200 hover:shadow-md hover:border-blue-100 group">
        <div className="space-y-4 flex-1">
          <h3 className="text-[11px] font-bold tracking-[0.1em] text-slate-500 uppercase">
            {title}
          </h3>
          <div className="flex items-baseline space-x-2">
            <span className="text-[28px] font-bold text-slate-900 leading-none tracking-tight">
              {value}
            </span>
          </div>
        </div>
        <div className={`w-12 h-12 rounded-full ${bgColor} flex items-center justify-center text-white shadow-sm transform transition-transform duration-300 group-hover:scale-110 flex-shrink-0 ml-4`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    );
  };

  // Sorting State
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Handle Sort Request
  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Helper for sort icon
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <ArrowUpDown className="h-4 w-4 text-gray-400 ml-1" />;
    if (sortConfig.direction === 'asc') return <ArrowUp className="h-4 w-4 text-brand ml-1" />;
    return <ArrowDown className="h-4 w-4 text-brand ml-1" />;
  };

  // Get Sorted Data
  const sortedOrientations = React.useMemo(() => {
    let sortableItems = [...orientations];
    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Specific handling for fields
        if (sortConfig.key === 'title') {
          aValue = (a.title || "").toLowerCase();
          bValue = (b.title || "").toLowerCase();
        } else if (sortConfig.key === 'type') {
          aValue = (a.type || "").toLowerCase();
          bValue = (b.type || "").toLowerCase();
        } else if (sortConfig.key === 'status') {
          aValue = (a.status || "").toLowerCase();
          bValue = (b.status || "").toLowerCase();
        } else if (sortConfig.key === 'date') {
          aValue = new Date(a.date || 0).getTime();
          bValue = new Date(b.date || 0).getTime();
          // Secondary sort by time if dates are equal
          if (aValue === bValue) {
            return sortConfig.direction === 'asc'
              ? (a.time || "").localeCompare(b.time || "")
              : (b.time || "").localeCompare(a.time || "");
          }
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [orientations, sortConfig]);

  // Truncate helper for tidy listing
  const truncate = (value, max = 30) => {
    if (!value && value !== 0) return "";
    let str = "";
    if (typeof value === 'object' && value !== null) {
      str = value.name || value.fullAddress || String(value);
    } else {
      str = String(value);
    }
    return str.length > max ? str.slice(0, max - 1) + "…" : str;
  };

  // Permission-based access control functions
  const canExportData = () => {
    return hasPermission('onboarding', 'read');
  };

  const canCreateOrientation = () => {
    return canCreate('onboarding');
  };

  const canViewOrientation = () => {
    return hasPermission('onboarding', 'read');
  };

  const canEditOrientation = () => {
    return hasPermission('onboarding', 'update');
  };

  const canDeleteOrientation = () => {
    return hasPermission('onboarding', 'delete');
  };

  const canSendInvites = () => {
    return hasPermission('onboarding', 'create') || hasPermission('onboarding', 'update');
  };

  const canCloneOrientation = () => {
    return (
      user?.role === "hr_manager" ||
      user?.role === "super_admin" ||
      user?.role === "hr_executive"
    );
  };

  // Reset page when filters/tab/search/date change
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, filterType, filterLocation, searchTerm, dateRange.startDate, dateRange.endDate, setCurrentPage]);

  // Load orientations from API
  useEffect(() => {
    loadOrientations();
    loadLocations();
  }, [activeTab, filterType, filterLocation, searchTerm, dateRange.startDate, dateRange.endDate, currentPage, itemsPerPage]);

  // Load locations from backend
  const loadLocations = async () => {
    try {
      const result = await locationService.getActiveLocations();

      if (result.success) {
        const locationNames = result.data.map(loc => {
          if (typeof loc === 'string') return loc;
          if (loc && typeof loc === 'object') {
            if (loc.value) return loc.value;
            return loc.name || loc.label || loc.value || String(loc);
          }
          return String(loc);
        });
        setLocations(locationNames);
      } else {

        // Fallback to hardcoded locations
        setLocations(['Mumbai Office', 'Delhi Office', 'Bangalore Office', 'Chennai Office', 'Remote']);
      }
    } catch (err) {

      // Fallback to hardcoded locations
      setLocations(['Mumbai Office', 'Delhi Office', 'Bangalore Office', 'Chennai Office', 'Remote']);
    }
  };

  const loadOrientations = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = { page: currentPage, limit: itemsPerPage };
      // Map active tab to backend status if applicable
      const tabStatusMap = {
        upcoming: 'scheduled',
        ongoing: 'ongoing',
        completed: 'completed',
        cancelled: 'cancelled'
      };
      const mappedStatus = tabStatusMap[activeTab];
      if (mappedStatus) params.status = mappedStatus;
      if (filterType !== "all") params.type = filterType;
      if (filterLocation !== 'all') params.location = filterLocation;
      if (searchTerm) params.search = searchTerm;
      if (dateRange.startDate) params.startDate = dateRange.startDate;
      if (dateRange.endDate) params.endDate = dateRange.endDate;

      const result = await orientationService.getOrientations(params);

      if (result && result.responseCode === 200 && result.data) {
        setOrientations(result.data.orientations || []);
        const p = result.data.pagination || {};
        // normalize keys
        const current = p.current || p.currentPage || p.page || currentPage;
        const pages = p.pages || p.totalPages || totalPages;
        const total = p.total || p.totalItems || totalItems;
        const limit = p.limit || p.itemsPerPage || itemsPerPage;
        if (current && current !== currentPage) setCurrentPage(current);
        if (limit && limit !== itemsPerPage) setItemsPerPage(limit);
        setTotalPages(pages || 1);
        setTotalItems(Number.isFinite(total) ? total : (result.data.orientations?.length || 0));

        // Extract and set statistics from API response
        if (result.data.statistics) {
          setStatistics({
            total: result.data.statistics.total || 0,
            scheduled: result.data.statistics.scheduled || 0,
            ongoing: result.data.statistics.ongoing || 0,
            completed: result.data.statistics.completed || 0,
            cancelled: result.data.statistics.cancelled || 0
          });
        }
      } else {
        setError("Failed to load orientations");
        showError("Failed to load orientations");
      }
    } catch (error) {
      setError("Error loading orientations");
      showError("Error loading orientations");
    } finally {
      setLoading(false);
    }
  };

  // State for selection and export
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [isBulkDelete, setIsBulkDelete] = useState(false);
  const [showInlineSelectedFormats, setShowInlineSelectedFormats] = useState(false);
  const [inlineFormatPanelEntered, setInlineFormatPanelEntered] = useState(false);
  const selectedExportWrapRef = useRef(null);

  // Get current page orientation IDs
  const currentPageOrientationIds = useMemo(
    () =>
      orientations
        .map((o) => {
          const raw = o?._id ?? o?.id;
          return raw != null && raw !== '' ? String(raw) : '';
        })
        .filter(Boolean),
    [orientations]
  );

  // Build base export filters for backend API
  const baseExportFilters = useMemo(() => {
    const tabStatusMap = {
      upcoming: 'scheduled',
      ongoing: 'ongoing',
      completed: 'completed',
      cancelled: 'cancelled'
    };
    const mappedStatus = tabStatusMap[activeTab];

    const apiFilters = {
      search: searchTerm || undefined,
      status: mappedStatus || undefined,
      type: filterType !== 'all' ? filterType : undefined,
      location: filterLocation !== 'all' ? filterLocation : undefined,
      startDate: dateRange.startDate || undefined,
      endDate: dateRange.endDate || undefined,
    };

    Object.keys(apiFilters).forEach((key) => {
      if (apiFilters[key] === undefined) delete apiFilters[key];
    });

    return apiFilters;
  }, [searchTerm, activeTab, filterType, filterLocation, dateRange.startDate, dateRange.endDate]);

  // Unified backend export function using /exports/generate endpoint
  const requestBackendExport = useCallback(
    async ({
      scope,
      format,
      selectedIds = [],
      overrideFilters = null,
      overridePage = null,
      overrideLimit = null,
      currentPageIds: overrideCurrentPageIds = null,
    }) => {
      const token = localStorage.getItem('auth_token');
      const pageIds =
        scope === 'currentPage'
          ? (overrideCurrentPageIds ?? currentPageOrientationIds)
          : [];
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/exports/generate`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            module: 'orientation',
            format,
            scope,
            filters: overrideFilters ?? baseExportFilters,
            page: overridePage ?? currentPage,
            limit: overrideLimit ?? itemsPerPage,
            sortBy: 'createdAt',
            sortOrder: 'desc',
            selectedIds,
            currentPageIds: Array.isArray(pageIds) ? pageIds : [],
          }),
        }
      );

      if (!response.ok) {
        const text = await response.text();
        let msg = text || 'Export request failed';
        try {
          const j = JSON.parse(text);
          if (j?.responseMessage || j?.message) {
            msg = j.responseMessage || j.message;
          }
        } catch {
          /* plain text */
        }
        throw new Error(msg);
      }

      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') || '';
      const filenameMatch =
        disposition.match(/filename\*=UTF-8''([^;]+)/i) ||
        disposition.match(/filename="?([^"]+)"?/i);
      const fallbackName = `orientation-export.${format === 'xlsx' ? 'xlsx' : format}`;
      const filename = filenameMatch?.[1]
        ? decodeURIComponent(filenameMatch[1]).replace(/(^"|"$)/g, '')
        : fallbackName;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    [
      baseExportFilters,
      currentPage,
      itemsPerPage,
      currentPageOrientationIds,
    ]
  );

  // Handle export for selected orientations with format selection
  const handleOrientationSelectedFormatExport = useCallback(
    async (format) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) return;
      const ids = Array.from(selectedRows).map(String).filter(Boolean);
      if (!ids.length) {
        showError('No selected orientations to export.');
        return;
      }
      setExportLoading(true);
      try {
        await requestBackendExport({
          scope: 'selected',
          format,
          selectedIds: ids,
        });
        showSuccess('Export started.');
        setShowInlineSelectedFormats(false);
      } catch (error) {
        console.error(error);
        showError(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [selectedRows, requestBackendExport]
  );

  // Handle scoped export from modal (all, filtered, currentPage, dateRange)
  const handleOrientationScopedExport = useCallback(
    async (scope, format, meta = {}) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) {
        return;
      }
      setExportLoading(true);
      try {
        if (scope === 'dateRange') {
          const { startDate, endDate } = meta;
          if (!startDate || !endDate) {
            showError('Please select both start and end dates.');
            return;
          }
          await requestBackendExport({
            scope: 'dateRange',
            format,
            overrideFilters: {
              ...baseExportFilters,
              startDate,
              endDate,
            },
          });
        } else if (scope === 'all') {
          await requestBackendExport({
            scope: 'all',
            format,
            overrideFilters: {},
            overridePage: 1,
            overrideLimit: itemsPerPage,
          });
        } else if (scope === 'filtered') {
          await requestBackendExport({
            scope: 'filtered',
            format,
          });
        } else if (scope === 'currentPage') {
          await requestBackendExport({
            scope: 'currentPage',
            format,
            currentPageIds: currentPageOrientationIds,
          });
        } else {
          return;
        }

        showSuccess('Export started.');
        setShowExportModal(false);
      } catch (error) {
        console.error(error);
        showError(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [
      orientations.length,
      baseExportFilters,
      requestBackendExport,
      itemsPerPage,
      currentPageOrientationIds,
    ]
  );

  // Click-outside handler for inline export format bar
  useEffect(() => {
    const onMouseDown = (event) => {
      if (!showInlineSelectedFormats) return;
      const target = event.target;
      const wrap = selectedExportWrapRef.current;
      const bar = target.closest('[data-export-format-bar]');
      if (wrap && !wrap.contains(target) && !bar) {
        setShowInlineSelectedFormats(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [showInlineSelectedFormats]);

  // Reset selection when filters/search/page change
  useEffect(() => {
    setSelectedRows(new Set());
  }, [currentPage, filterType, filterLocation, searchTerm, activeTab]);

  // Selection Handlers
  const toggleRowSelection = (id) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRows(newSelected);
  };

  const toggleSelectAll = () => {
    const currentPageIds = orientations.map(o => o.id);
    const selectedOnPage = currentPageIds.filter(id => selectedRows.has(id));

    if (selectedOnPage.length > 0) {
      // If any selected on current page, clear EVERYTHING
      setSelectedRows(new Set());
    } else {
      // Otherwise select all on current page
      const newSelected = new Set(selectedRows);
      currentPageIds.forEach(id => newSelected.add(id));
      setSelectedRows(newSelected);
    }
  };

  // Helper to map data for export
  const mapOrientationsForExport = (data) => {
    return data.map(orientation => {
      // Format trainer field - prioritize trainerData names
      let trainerDisplay = '';
      if (Array.isArray(orientation.trainerData) && orientation.trainerData.length > 0) {
        trainerDisplay = orientation.trainerData.map(t => t.name).join(', ');
      } else if (Array.isArray(orientation.trainer)) {
        trainerDisplay = orientation.trainer.join(', ');
      } else {
        trainerDisplay = orientation.trainer || '';
      }

      return {
        'Title': orientation.title?.replace(/\b\w/g, l => l.toUpperCase()),
        'Description': orientation.description || '',
        'Trainer': trainerDisplay || '',
        'Department': orientation.department || '',
        'Type': orientation.type?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
        'Location': (typeof orientation.location === 'object' && orientation.location !== null ? orientation.location.name : orientation.location)?.replace(/\b\w/g, l => l.toUpperCase()),
        'Status': orientation.status?.replace(/\b\w/g, l => l.toUpperCase()),
        'Reason': orientation.reason || '',
        'Date': formatDate(orientation.date, { includeTime: false }) || 'N/A',
        'Time': formatTimeToAMPM(orientation.time),
        'Duration': orientation.duration === 0.5 ? '30 min' : `${orientation.duration} ${orientation.duration === 1 ? 'hour' : 'hours'}`
      };
    });
  };


  const handleExportClick = () => {
    if (selectedRows.size > 0) {
      setShowInlineSelectedFormats((open) => !open);
      return;
    }
    setShowInlineSelectedFormats(false);
    setShowExportModal(true);
  };

  const handleExportCurrentPage = () => {
    if (orientations.length === 0) {
      showError('No data to export on current page');
      return;
    }

    const exportData = mapOrientationsForExport(orientations);

    exportToCSV(exportData, 'orientation_sessions_page', {
      title: 'Orientation Sessions List',
      type: `Current Page (${activeTab})`
    });

    setShowExportModal(false);
    showSuccess('Current page exported successfully');
  };

  const handleExportRange = async (startDate, endDate) => {
    setExportLoading(true);
    try {
      const params = {
        startDate,
        endDate,
        format: 'csv'
      };

      // Add current filters
      const tabStatusMap = {
        upcoming: 'scheduled',
        ongoing: 'ongoing',
        completed: 'completed',
        cancelled: 'cancelled'
      };
      const mappedStatus = tabStatusMap[activeTab];
      if (mappedStatus) params.status = mappedStatus;
      if (filterType !== "all") params.type = filterType;
      if (filterLocation !== 'all') params.location = filterLocation;
      if (searchTerm) params.search = searchTerm;

      // Use the service to get the blob
      const blob = await orientationService.exportOrientations(params);

      // Create download link
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', generateExportFilename('orientation_sessions_range'));
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);

      setShowExportModal(false);
      showSuccess('Data exported successfully');
    } catch (error) {

      showError('Failed to export data');
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportAll = async () => {
    setExportLoading(true);
    try {
      const params = {
        format: 'csv',
        limit: 10000 // Ensure we get all records if backend supports limit override or ignores pagination for export
      };

      // Add current filters
      const tabStatusMap = {
        upcoming: 'scheduled',
        ongoing: 'ongoing',
        completed: 'completed',
        cancelled: 'cancelled'
      };
      const mappedStatus = tabStatusMap[activeTab];
      if (mappedStatus) params.status = mappedStatus;
      if (filterType !== "all") params.type = filterType;
      if (filterLocation !== 'all') params.location = filterLocation;
      if (searchTerm) params.search = searchTerm;

      // Use the service
      const blob = await orientationService.exportOrientations(params);

      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', generateExportFilename('orientation_sessions_all'));
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);

      setShowExportModal(false);
      showSuccess('All data exported successfully');
    } catch (error) {

      showError('Failed to export data');
    } finally {
      setExportLoading(false);
    }
  };

  const handleCreateOrientation = () => {
    navigate("/create_orientation");
  };

  const handleViewOrientation = (orientationId) => {
    navigate(`/view_orientation/${orientationId}`);
  };

  const handleEditOrientation = (orientationId) => {
    navigate(`/create_orientation/${orientationId}`);
  };

  const handleDeleteOrientation = (orientationId) => {
    setDeleteOrientationId(orientationId);
    setIsBulkDelete(false);
    setShowDeleteModal(true);
  };

  const handleBulkDeleteOrientation = () => {
    if (selectedRows.size === 0) return;
    setIsBulkDelete(true);
    setShowDeleteModal(true);
  };

  const confirmDeleteOrientation = async () => {
    setIsDeleting(true);
    try {
      if (isBulkDelete) {
        const idsToDelete = Array.from(selectedRows);
        let successCount = 0;
        let errorCount = 0;

        for (const id of idsToDelete) {
          try {
            const result = await orientationService.deleteOrientation(id);
            if (result && result.responseCode === 200) {
              successCount++;
            } else {
              errorCount++;
            }
          } catch (error) {

            errorCount++;
          }
        }

        if (successCount > 0) {
          showSuccess(`${successCount} orientations deleted successfully`);
          setSelectedRows(new Set());
          loadOrientations();
        }
        if (errorCount > 0) {
          showError(`Failed to delete ${errorCount} orientations`);
        }
      } else {
        if (!deleteOrientationId) return;
        const result = await orientationService.deleteOrientation(deleteOrientationId);
        if (result && result.responseCode === 200) {
          showSuccess('Orientation deleted successfully');
          loadOrientations();
        } else {
          showError(result?.responseMessage || 'Failed to delete orientation');
        }
      }
      setShowDeleteModal(false);
      setDeleteOrientationId(null);
      setIsBulkDelete(false);
    } catch (error) {

      showError('An error occurred while deleting');
    } finally {
      setIsDeleting(false);
    }
  };

  const cancelDeleteOrientation = () => {
    setShowDeleteModal(false);
    setDeleteOrientationId(null);
    setIsBulkDelete(false);
  };

  const handleSendInvites = (orientationId) => {
    navigate(`/send_orientation_invites/${orientationId}`);
  };

  const handleCloneOrientation = (orientationId) => {
    navigate(`/clone_orientation/${orientationId}`);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "scheduled":
        return "bg-blue-100 text-blue-800";
      case "ongoing":
        return "bg-yellow-100 text-yellow-800";
      case "completed":
        return "bg-green-100 text-green-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "scheduled":
        return <Calendar className="h-4 w-4" />;
      case "ongoing":
        return <Play className="h-4 w-4" />;
      case "completed":
        return <CheckCircle className="h-4 w-4" />;
      case "cancelled":
        return <AlertCircle className="h-4 w-4" />;
      default:
        return <Calendar className="h-4 w-4" />;
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case "in_person":
        return <MapPin className="h-4 w-4" />;
      case "virtual":
        return <Video className="h-4 w-4" />;
      default:
        return <Calendar className="h-4 w-4" />;
    }
  };

  const formatTimeToAMPM = (timeString) => {
    if (!timeString) return 'N/A';

    try {
      // Handle formats like "14:30" or "14:30:00"
      const timeParts = timeString.split(':');
      if (timeParts.length < 2) return timeString;

      const hours = parseInt(timeParts[0], 10);
      const minutes = timeParts[1];

      if (isNaN(hours) || hours < 0 || hours > 23) return timeString;

      const period = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;

      return `${displayHours}:${minutes} ${period}`;
    } catch (error) {
      return timeString;
    }
  };

  // Server drives status via params; tabs map to status in API call
  // Server-side filtering & pagination are applied; client only displays current page results
  // Statistics are now provided by the API response

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4"></div>
          <p className="text-gray-600">Loading orientations...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-4">❌</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Error Loading Orientations
          </h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <PermissionGate permission="onboarding" action="read">
            <Button
              onClick={loadOrientations}
              className="bg-brand hover:bg-brand-dark"
            >
              Try Again
            </Button>
          </PermissionGate>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Orientation Management
          </h1>
          <p className="text-gray-600">
            Schedule and manage employee orientation sessions
          </p>
        </div>
        <div className="flex items-center space-x-3">
          {selectedRows.size > 0 && canDeleteOrientation() && (
            <PermissionGate permission="onboarding" action="delete">
              <Button
                variant="primary"
                icon={<Trash2 className="h-4 w-4" />}
                onClick={handleBulkDeleteOrientation}
                className="bg-red-600 hover:bg-red-700 border-red-600 flex items-center gap-2"
              >
                Delete ({selectedRows.size})
              </Button>
            </PermissionGate>
          )}          {canExportData() && (
            <PermissionGate permission="onboarding" action="read">
              <div className="relative" ref={selectedExportWrapRef}>
                <Button
                  variant="outline"
                  icon={
                    exportLoading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-[#0F2942] border-t-transparent" />
                    ) : (
                      <Download className="h-4 w-4" />
                    )
                  }
                  onClick={handleExportClick}
                  disabled={exportLoading}
                  className={`flex-shrink-0 ${selectedRows.size > 0 ? 'bg-blue-50 border-blue-200 text-[#0F2942]' : ''}`}
                  aria-label={
                    selectedRows.size > 0
                      ? `Export ${selectedRows.size} selected orientations`
                      : 'Export orientations'
                  }
                >
                  {selectedRows.size > 0 ? `Export Selected (${selectedRows.size})` : 'Export'}
                </Button>
                <SelectedExportFormatBar
                  show={selectedRows.size > 0 && showInlineSelectedFormats}
                  panelEntered={inlineFormatPanelEntered}
                  disabled={exportLoading}
                  onSelect={handleOrientationSelectedFormatExport}
                />
              </div>
            </PermissionGate>
          )}

          {canCreateOrientation() && (
            <PermissionGate permission="onboarding" action="create">
              <Button
                variant="outline"
                icon={<Upload className="h-4 w-4" />}
                onClick={() => setShowBulkUploadModal(true)}
                className="flex items-center space-x-2"
              >
                Bulk Upload
              </Button>
            </PermissionGate>
          )}

          {canCreateOrientation() && (
            <PermissionGate permission="onboarding" action="create">
              <Button
                variant="primary"
                icon={<Plus className="h-4 w-4" />}
                onClick={handleCreateOrientation}
                className="!bg-[#0F2942] hover:!bg-[#0F2942]/90 text-white"
              >
                Create Orientation
              </Button>
            </PermissionGate>
          )}
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          icon={Calendar}
          title="Total Sessions"
          value={formatIndianNumber(statistics.total)}
          color="bg-[#0F2942]"
        />
        <KpiCard
          icon={Clock}
          title="Scheduled"
          value={formatIndianNumber(statistics.scheduled)}
          color="bg-yellow-600"
        />
        <KpiCard
          icon={Play}
          title="Ongoing"
          value={formatIndianNumber(statistics.ongoing)}
          color="bg-green-600"
        />
        <KpiCard
          icon={CheckCircle}
          title="Completed"
          value={formatIndianNumber(statistics.completed)}
          color="bg-purple-600"
        />
      </div>

      {/* Tabs (SaaS Segmented Pill Container) */}
      <div className="bg-[#F8FAFC] rounded-2xl border border-slate-200/80 p-2 shadow-2xs">
        <div className="bg-slate-100/90 p-1.5 rounded-xl border border-slate-200/80 inline-flex space-x-1.5">
          {[
            {
              key: "upcoming",
              label: "Upcoming",
              count: statistics.scheduled,
            },
            {
              key: "ongoing",
              label: "Ongoing",
              count: statistics.ongoing,
            },
            {
              key: "completed",
              label: "Completed",
              count: statistics.completed,
            },
          ].map((tab) => (
            <PermissionGate key={tab.key} permission="onboarding" action="read">
              <button
                onClick={() => setActiveTab(tab.key)}
                className={`py-2 px-4 rounded-lg font-bold text-xs flex items-center space-x-2 transition-all cursor-pointer ${
                  activeTab === tab.key
                    ? 'bg-white text-blue-700 shadow-2xs border border-slate-200/70 scale-[1.01]'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-white/50'
                }`}
              >
                <span>{tab.label}</span>
                <span
                  className={`py-0.5 px-2 rounded-full text-[10px] font-extrabold ${
                    activeTab === tab.key
                      ? 'bg-blue-50 text-blue-700'
                      : 'bg-slate-200 text-slate-600'
                  }`}
                >
                  {tab.count}
                </span>
              </button>
            </PermissionGate>
          ))}
        </div>
      </div>

        <div className="p-6">
          {/* Filters */}
          <div className="flex flex-wrap items-end gap-4 w-full mb-6">
            {/* Search */}
            <div className="relative flex-[2] min-w-[200px]">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search by Session Detail..."
                value={searchTerm}
                onChange={(e) => {
                  // Allow alphanumeric, spaces, and common search/document symbols
                  const value = e.target.value.replace(/[^a-zA-Z0-9\s,.@\/\-_\(\)\[\]#:&?!]/g, '').slice(0, 100);
                  setSearchTerm(value);
                }}
                onKeyPress={(e) => {
                  // Allow alphanumeric, spaces, and common search/document symbols
                  if (!/^[a-zA-Z0-9\s,.@\/\-_\(\)\[\]#:&?!]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                maxLength={100}
                autoFocus
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand placeholder:font-light placeholder:text-gray-400"
              />
            </div>

            {/* Type Filter */}
            <div className="flex-shrink-0">
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
              >
                <option value="all">All Types</option>
                <option value="in_person">In-Person</option>
                <option value="virtual">Virtual</option>
              </select>
            </div>

            {/* Location Filter */}
            <div className="flex-shrink-0">
              <select
                value={filterLocation}
                onChange={(e) => setFilterLocation(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
              >
                <option value="all">All Locations</option>
                {locations.map(location => (
                  <option key={location} value={location}>{location?.replace(/\b\w/g, l => l.toUpperCase())}</option>
                ))}
              </select>
            </div>

            {/* Date Range Filter */}
            <div className="flex-shrink-0">
              <DateRangeFilter
                startDate={dateRange.startDate}
                endDate={dateRange.endDate}
                allowFutureDates={true}
                inputClassName="input-field text-sm w-36 cursor-pointer outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
                onChange={setDateRange}
              />
            </div>

            {/* Clear Filters */}
            {(searchTerm || filterType !== "all" || filterLocation !== "all" || dateRange.startDate || dateRange.endDate) && (
              <div className="relative flex-shrink-0">
                <Button
                  variant="outline"
                  icon={<X className="h-4 w-4" />}
                  onClick={() => {
                    resetOrientationFilters();
                  }}
                >
                  Clear
                </Button>
              </div>
            )}
          </div>

          {/* Orientations List */}
          {!loading && orientations.length === 0 ? (
            <div className="bg-white rounded-lg border border-gray-200">
              <div className="text-center py-12">
                <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No orientations found
                </h3>
                <p className="text-gray-500 mb-4">
                  {searchTerm || filterType !== "all" || filterLocation !== "all" || dateRange.startDate || dateRange.endDate
                    ? 'Try adjusting your search or filter criteria.'
                    : 'Get started by creating your first orientation session.'}
                </p>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto overflow-y-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-transparent" style={{ backgroundColor: 'rgba(59, 130, 246, 0.14)' }}>
                  <tr>
                    <th className="px-6 py-3 text-left w-12">
                      <button
                        onClick={toggleSelectAll}
                        className="text-gray-400 hover:text-gray-600 focus:outline-none transition-colors"
                        title={selectedRows.size === orientations.length ? "Deselect All" : "Select All"}
                      >
                        {orientations.length > 0 && orientations.every(o => selectedRows.has(o.id)) ? (
                          <CheckSquare className="h-5 w-5 text-brand" />
                        ) : (
                          <Square className="h-5 w-5" />
                        )}
                      </button>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 transition-colors"
                      onClick={() => requestSort('title')}
                      title="Sort by Title"
                    >
                      <div className="flex items-center">
                        Session Details
                        {getSortIcon('title')}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 transition-colors"
                      onClick={() => requestSort('type')}
                      title="Sort by Type"
                    >
                      <div className="flex items-center">
                        Type & Location
                        {getSortIcon('type')}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 transition-colors"
                      onClick={() => requestSort('status')}
                      title="Sort by Status"
                    >
                      <div className="flex items-center">
                        Status
                        {getSortIcon('status')}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 transition-colors"
                      onClick={() => requestSort('date')}
                      title="Sort by Date"
                    >
                      <div className="flex items-center">
                        Schedule
                        {getSortIcon('date')}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 transition-colors"
                      onClick={() => requestSort('createdAt')}
                      title="Sort by Created At"
                    >
                      <div className="flex items-center">
                        Created At
                        {getSortIcon('createdAt')}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider"
                      title="Available actions: View, Edit, Send Invites, or Delete orientation"
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sortedOrientations.map((orientation) => (
                    <tr key={orientation.id} className="hover:bg-blue-50/30">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleRowSelection(orientation.id);
                          }}
                          className={`focus:outline-none transition-colors ${selectedRows.has(orientation.id) ? 'text-brand' : 'text-gray-400 hover:text-gray-600'
                            }`}
                        >
                          {selectedRows.has(orientation.id) ? (
                            <CheckSquare className="h-5 w-5" />
                          ) : (
                            <Square className="h-5 w-5" />
                          )}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{truncate(orientation.title?.replace(/\b\w/g, l => l.toUpperCase()), 60)}</div>
                          <div className="text-sm text-gray-500">{truncate(orientation.description, 60)}</div>
                          <div className="text-xs text-gray-400">
                            Trainer: {truncate(
                              Array.isArray(orientation.trainerData) && orientation.trainerData.length > 0
                                ? orientation.trainerData.map(t => t.name).join(', ')
                                : (Array.isArray(orientation.trainer) ? orientation.trainer.join(', ') : (orientation.trainer || 'N/A')),
                              60
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {getTypeIcon(orientation.type)}
                          <div className="ml-2">
                            <div className="text-sm text-gray-900 capitalize">
                              {orientation.type.replace("_", " ")}
                            </div>
                            <div className="text-sm text-gray-500">
                              {truncate(orientation.location, 24)}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                            orientation.status
                          )}`}
                        >
                          {getStatusIcon(orientation.status)}
                          <span className="ml-1 capitalize">
                            {orientation.status}
                          </span>
                        </span>
                        {orientation.reason && (
                          <div className="text-xs text-red-600 mt-1">
                            {truncate(orientation.reason, 48)}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm text-gray-900">
                            {formatDate(orientation.date, { includeTime: false }) || 'N/A'}
                          </div>
                          <div className="text-sm text-gray-500">
                            at {formatTimeToAMPM(orientation.time)}
                          </div>
                          <div className="text-xs text-gray-400">
                            {orientation.duration === 0.5 ? '30 min' : `${orientation.duration} ${orientation.duration === 1 ? 'hour' : 'hours'}`}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {orientation.createdAt ? formatDate(orientation.createdAt, { includeTime: true }) : 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          {canViewOrientation() && (
                            <PermissionGate permission="onboarding" action="read">
                              <button
                                onClick={() =>
                                  handleViewOrientation(orientation.id)
                                }
                                className="text-[#0F2942] hover:text-blue-900"
                                title="View Orientation Details"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                            </PermissionGate>
                          )}
                          {canEditOrientation() &&
                            orientation.status !== "completed" && (
                              <PermissionGate permission="onboarding" action="update">
                                <button
                                  onClick={() =>
                                    handleEditOrientation(orientation.id)
                                  }
                                  className="text-indigo-600 hover:text-indigo-900"
                                  title="Edit Orientation"
                                >
                                  <Edit className="h-4 w-4" />
                                </button>
                              </PermissionGate>
                            )}
                          {canSendInvites() &&
                            orientation.status === "scheduled" && (
                              <PermissionGate permission="onboarding" action="update">
                                <button
                                  onClick={() => handleSendInvites(orientation.id)}
                                  className="text-green-600 hover:text-green-900"
                                  title="Send Invites"
                                >
                                  <Send className="h-4 w-4" />
                                </button>
                              </PermissionGate>
                            )}

                          {canDeleteOrientation() &&
                            (orientation.status === "scheduled" ||
                              orientation.status === "completed" ||
                              orientation.status === "cancelled") && (
                              <PermissionGate permission="onboarding" action="delete">
                                <button
                                  onClick={() =>
                                    handleDeleteOrientation(orientation.id)
                                  }
                                  className="text-red-600 hover:text-red-900"
                                  title="Delete Orientation"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </PermissionGate>
                            )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!loading && (
                <PaginationControls
                  border={false}
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={totalItems}
                  limit={itemsPerPage}
                  hasNextPage={currentPage < totalPages}
                  hasPrevPage={currentPage > 1}
                  onPageChange={(newPage) => setCurrentPage(newPage)}
                  onLimitChange={(newLimit) => setItemsPerPage(newLimit)}
                  limitOptions={[10, 20, 50, 100]}
                />
              )}
            </div>
          )}
        </div>

      {/* Custom Alert */}
      <CustomAlert
        alertState={alertState}
        hideAlert={hideAlert}
      />

      {/* WarningPopup for Deletion */}
      <WarningPopup
        isOpen={showDeleteModal}
        onClose={cancelDeleteOrientation}
        onConfirm={confirmDeleteOrientation}
        title={isBulkDelete ? "Delete Orientations" : "Delete Orientation"}
        message={isBulkDelete
          ? `Are you sure you want to delete ${selectedRows.size} selected orientation sessions? This action cannot be undone.`
          : `Are you sure you want to delete this orientation session? This action cannot be undone.`
        }
        confirmText={isDeleting ? "Deleting..." : "Delete"}
        cancelText="Cancel"
        confirmVariant="danger"
      />

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        loading={exportLoading}
        title="Export Orientation Data"
        showDateRange
        scopedFormatExport={{
          onExport: handleOrientationScopedExport,
          formats: ['csv', 'xlsx', 'pdf'],
          showCurrentPage: true,
          showFiltered: true,
          showDateRange: true,
          showAll: true,
        }}
      />

      {/* Bulk Upload Modal */}
      {showBulkUploadModal && (
        <BulkUploadModal
          onClose={() => setShowBulkUploadModal(false)}
          onSuccess={() => loadOrientations()}
          config={{
            title: 'Bulk Upload Orientations',
            subtitle: 'Import multiple orientation sessions using a CSV file',
            templateEndpoint: '/orientation/bulk-upload/template',
            validateEndpoint: '/orientation/bulk-upload/validate',
            uploadEndpoint: '/orientation/bulk-upload',
            templateFilename: 'orientation_bulk_upload_template.csv',
            dataKey: 'orientations',
            instructions: [
              'Fill in orientation details in the CSV',
              'Fields: title, description, type, location, date, time, duration, capacity, trainer, department',
              'Ensure date format is YYYY-MM-DD'
            ]
          }}
        />
      )}
    </div>
  );
};

export default Orientation;
